import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Shield, Package, Lock, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';

const LoginPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('customer'); // 'customer' or 'admin'
  
  // Customer login state
  const [orderNumber, setOrderNumber] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [orderStatus, setOrderStatus] = useState(null);
  const [customerError, setCustomerError] = useState('');
  
  // Admin login state
  const [adminUsername, setAdminUsername] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [adminError, setAdminError] = useState('');

  const apiUrl = process.env.REACT_APP_BACKEND_URL || '';

  // Handle customer order tracking
  const handleCustomerLogin = async (e) => {
    e.preventDefault();
    setCustomerError('');
    setOrderStatus(null);

    try {
      const response = await fetch(`${apiUrl}/api/orders/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderNumber, email: customerEmail })
      });

      if (response.ok) {
        const data = await response.json();
        setOrderStatus(data);
      } else {
        setCustomerError('Order not found. Please check your order number and email.');
      }
    } catch (error) {
      setCustomerError('Unable to retrieve order status. Please try again.');
    }
  };

  // Handle admin login
  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setAdminError('');

    try {
      const credentials = btoa(`${adminUsername}:${adminPassword}`);
      const response = await fetch(`${apiUrl}/api/admin/verify`, {
        headers: { 'Authorization': `Basic ${credentials}` }
      });

      if (response.ok) {
        localStorage.setItem('adminAuth', credentials);
        navigate('/admin');
      } else {
        setAdminError('Invalid admin credentials');
      }
    } catch (error) {
      setAdminError('Login failed. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b">
            <button
              onClick={() => {
                setActiveTab('customer');
                setCustomerError('');
                setOrderStatus(null);
              }}
              className={`flex-1 py-4 px-6 font-semibold transition-colors ${
                activeTab === 'customer'
                  ? 'bg-[hsl(45,100%,70%)] text-black border-b-2 border-black'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <User className="w-5 h-5" />
                <span>Customer</span>
              </div>
            </button>
            <button
              onClick={() => {
                setActiveTab('admin');
                setAdminError('');
              }}
              className={`flex-1 py-4 px-6 font-semibold transition-colors ${
                activeTab === 'admin'
                  ? 'bg-[hsl(45,100%,70%)] text-black border-b-2 border-black'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Shield className="w-5 h-5" />
                <span>Admin</span>
              </div>
            </button>
          </div>

          {/* Content */}
          <div className="p-8">
            {activeTab === 'customer' ? (
              // Customer Order Tracking
              <>
                <div className="flex items-center justify-center mb-6">
                  <div className="w-16 h-16 bg-[hsl(45,100%,70%)]/20 rounded-full flex items-center justify-center">
                    <Package className="w-8 h-8 text-[hsl(45,100%,70%)]" />
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-center mb-2">Track Your Order</h2>
                <p className="text-gray-600 text-center mb-6">
                  Enter your order details to check status
                </p>

                {!orderStatus ? (
                  <form onSubmit={handleCustomerLogin} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Order Number
                      </label>
                      <input
                        type="text"
                        value={orderNumber}
                        onChange={(e) => setOrderNumber(e.target.value)}
                        placeholder="e.g., ORD-12345"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[hsl(45,100%,70%)] focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={customerEmail}
                        onChange={(e) => setCustomerEmail(e.target.value)}
                        placeholder="your@email.com"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[hsl(45,100%,70%)] focus:border-transparent"
                        required
                      />
                    </div>

                    {customerError && (
                      <div className="flex items-start gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
                        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>{customerError}</span>
                      </div>
                    )}

                    <button
                      type="submit"
                      className="w-full bg-[hsl(45,100%,70%)] text-black font-bold py-3 rounded-lg hover:bg-[hsl(45,100%,60%)] transition-colors duration-300"
                    >
                      Track Order
                    </button>
                  </form>
                ) : (
                  // Order Status Display
                  <div className="space-y-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                        <div className="flex-1">
                          <h3 className="font-bold text-green-900 mb-1">Order Found!</h3>
                          <p className="text-sm text-green-700">Order #{orderStatus.orderNumber}</p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex justify-between items-center py-2 border-b">
                        <span className="text-gray-600">Status:</span>
                        <span className={`font-semibold px-3 py-1 rounded-full text-sm ${
                          orderStatus.status === 'delivered' ? 'bg-green-100 text-green-700' :
                          orderStatus.status === 'shipped' ? 'bg-blue-100 text-blue-700' :
                          orderStatus.status === 'processing' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {orderStatus.status.toUpperCase()}
                        </span>
                      </div>

                      <div className="flex justify-between items-center py-2 border-b">
                        <span className="text-gray-600">Order Date:</span>
                        <span className="font-medium">{orderStatus.orderDate}</span>
                      </div>

                      {orderStatus.trackingNumber && (
                        <div className="flex justify-between items-center py-2 border-b">
                          <span className="text-gray-600">Tracking:</span>
                          <span className="font-medium">{orderStatus.trackingNumber}</span>
                        </div>
                      )}

                      <div className="flex justify-between items-center py-2 border-b">
                        <span className="text-gray-600">Total:</span>
                        <span className="font-bold text-lg">{orderStatus.total}</span>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4 mt-4">
                      <h4 className="font-semibold mb-2">Items:</h4>
                      <ul className="space-y-1 text-sm text-gray-700">
                        {orderStatus.items?.map((item, idx) => (
                          <li key={idx}>• {item}</li>
                        ))}
                      </ul>
                    </div>

                    <button
                      onClick={() => {
                        setOrderStatus(null);
                        setOrderNumber('');
                        setCustomerEmail('');
                      }}
                      className="w-full bg-gray-200 text-gray-700 font-semibold py-3 rounded-lg hover:bg-gray-300 transition-colors duration-300"
                    >
                      Track Another Order
                    </button>
                  </div>
                )}
              </>
            ) : (
              // Admin Login
              <>
                <div className="flex items-center justify-center mb-6">
                  <div className="w-16 h-16 bg-[hsl(45,100%,70%)]/20 rounded-full flex items-center justify-center">
                    <Lock className="w-8 h-8 text-[hsl(45,100%,70%)]" />
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-center mb-2">Admin Access</h2>
                <p className="text-gray-600 text-center mb-6">
                  Product Management Panel
                </p>

                <form onSubmit={handleAdminLogin} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Username
                    </label>
                    <input
                      type="text"
                      value={adminUsername}
                      onChange={(e) => setAdminUsername(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[hsl(45,100%,70%)] focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={adminPassword}
                        onChange={(e) => setAdminPassword(e.target.value)}
                        className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[hsl(45,100%,70%)] focus:border-transparent"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  {adminError && (
                    <div className="flex items-start gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>{adminError}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    className="w-full bg-[hsl(45,100%,70%)] text-black font-bold py-3 rounded-lg hover:bg-[hsl(45,100%,60%)] transition-colors duration-300"
                  >
                    Login to Admin Panel
                  </button>
                </form>
              </>
            )}
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/')}
            className="text-white hover:text-[hsl(45,100%,70%)] transition-colors"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
